import type {
  LangCopy,
  TranslationContent
} from '@CS/components/TranslationProvider/types';

import { useTranslation, useLoader, useClassNames } from '@CS/hooks';
import { type PopupProps, Popup } from '@CS/components/Popups';
import type { Quiz as Question } from '@/services/api/types';
import { isSafari } from '@CS/utils/Device';
import { useNavigateQuery } from '@/hooks';
import Button from '@CS/components/Button';
import Timer from '@CS/components/Timer';
import type { AxiosError } from 'axios';
import CSS from './Quiz.module.css';
import { useState } from 'react';
import API from '@/services/api';
import Content from './content';

type QuizProps = PopupProps & {
  onError: (error: AxiosError) => void;
  onTimeout: () => void;
  onCorrect: () => void;
  onWrong: () => void;
  quiz: Question;
  index: number;
};

export const Quiz = ({
  onTimeout,
  onCorrect,
  onWrong,
  onError,
  close,
  index,
  quiz,
  ...props
}: QuizProps) => {
  const navigate = useNavigateQuery();

  const [selected, setSelected] = useState(0);
  const [answered, setAnswered] = useState(false);
  const onBeforeClose = () => !close && navigate('/');
  const onClick = (index: number) => setSelected(index + 1);

  const expiration = Date.now() + (quiz.timeToAnswerSec ?? 30) * 1e3 + 1e3;
  const { questionIndex, timeLeft, confirm } = useTranslation(Content);
  const { question } = useTranslation<{ question: LangCopy }>(
    quiz.translations as { question: LangCopy }
  );

  const answers = Object.values(useTranslation(quiz.answerOptions?.reduce(
    (translations: Record<string, TranslationContent>, answer, a) =>
      Object.assign(translations, { [a]: answer.translations })
    , {}
  ) as TranslationContent));

  const loader = useLoader(answered, { opacity: 0.5 });

  const onSubmit = () =>
    ~setAnswered(true) &&
      API.answerCampaignQuiz(quiz.id, quiz.answerOptions?.[selected - 1].id ?? '')
        .then(({ correct }) =>
          correct && ~onCorrect() || onWrong()
        )
        .catch(onError)
        .finally(() => setAnswered(false));

  return (
    <Popup
      {...props}
      opacity="0"
      close={close}
      ignoreClickOutside
      className={CSS.quizContainer}
      onBeforeClose={onBeforeClose}
    >
      <img alt={question ?? ''} src={quiz.imageUrls?.[0]} />

      <div className={useClassNames({
        [CSS.quizContent]: true,
        [CSS.revertPadding]: isSafari
      })}>
        <span>{questionIndex} {index}</span>
        <h4>{question}</h4>

        {answers.map((answer, a) => (
          <div
            key={answer}
            onClick={onClick.bind(null, a)}
            className={useClassNames([
              selected === a + 1 && CSS.selected,
              CSS.quizAnswer
            ])}
          >
            <i /><span>{answer}</span>
          </div>
        ))}

        <div className={CSS.timerContainer}>
          <span>{timeLeft}</span>
          <Timer
            expiration={expiration}
            onComplete={onTimeout}
            begin={!answered}
          />
        </div>

        <Button
          disabled={!selected}
          onClick={onSubmit}
          text={confirm}
        />
      </div>

      {loader}
    </Popup>
  );
};
